// Where an effect plays. Event bindings fill one preallocated placement per kind and hand it to the effect
// primitives, which copy what they keep; nothing holds a placement past the play call.

import { Vector3 } from 'three'
import { NEUTRAL_OWNER, NO_SIM_ID, type OwnerValue, type SimId } from '@taxwar/net'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../render/geo/globeSpace'

/** A point on (or above) the globe: rings, bursts, rain and labels. */
export interface PointPlacement {
  readonly kind: 'point'
  /** Whose colour 'owner'-coloured effects take. */
  owner: OwnerValue
  /** Unit normal of the anchor. */
  readonly at: Vector3
  /** Radius (render units) the anchor sits at: the network altitude, or a missile's altitude for interceptions. */
  altitude: number
  /** Angular size of the thing the effect is about (radians): effect radii are multiples of it. */
  extent: number
  /** Money amount the event carries (floaters print it); 0 for events without one. */
  amount: number
  /** Duration of a sustained effect (sanctions rain) in seconds; 0 for events without one. */
  seconds: number
}

/** Two points: beams, launch streaks and edge sweeps. */
export interface PathPlacement {
  readonly kind: 'path'
  owner: OwnerValue
  /** Unit normals of the ends and the radii they sit at. */
  readonly from: Vector3
  fromAltitude: number
  readonly to: Vector3
  toAltitude: number
}

/** A missile strike in flight: from the owner's capital to its target, followed by its entity position. */
export interface FlightPlacement {
  readonly kind: 'flight'
  owner: OwnerValue
  strike: SimId
  readonly origin: Vector3
  readonly target: Vector3
  /** Salvos the strike resolves (impact or interception) before its flight ends. */
  salvos: number
}

export interface PlacementByKind {
  readonly point: PointPlacement
  readonly path: PathPlacement
  readonly flight: FlightPlacement
}
export type PlacementKind = keyof PlacementByKind

export function createPointPlacement(): PointPlacement {
  return { kind: 'point', owner: NEUTRAL_OWNER, at: new Vector3(), altitude: GLOBE_ALTITUDE.network, extent: 0, amount: 0, seconds: 0 }
}

export function createPlacements(): PlacementByKind {
  return {
    point: createPointPlacement(),
    path: { kind: 'path', owner: NEUTRAL_OWNER, from: new Vector3(), fromAltitude: GLOBE_ALTITUDE.network, to: new Vector3(), toAltitude: GLOBE_ALTITUDE.network },
    flight: { kind: 'flight', owner: NEUTRAL_OWNER, strike: NO_SIM_ID, origin: new Vector3(), target: new Vector3(), salvos: 0 },
  }
}

/** Fills every field of a point placement on the network surface. */
export function placePoint(out: PointPlacement, owner: OwnerValue, latDeg: number, lonDeg: number, extent: number, amount: number, seconds: number): PointPlacement {
  out.owner = owner
  latLonToVector3(latDeg, lonDeg, 1, out.at)
  out.altitude = GLOBE_ALTITUDE.network
  out.extent = extent
  out.amount = amount
  out.seconds = seconds
  return out
}

/** Fills every field of a path placement between two network-surface points. */
export function placePath(out: PathPlacement, owner: OwnerValue, fromLat: number, fromLon: number, toLat: number, toLon: number): PathPlacement {
  out.owner = owner
  latLonToVector3(fromLat, fromLon, 1, out.from)
  out.fromAltitude = GLOBE_ALTITUDE.network
  latLonToVector3(toLat, toLon, 1, out.to)
  out.toAltitude = GLOBE_ALTITUDE.network
  return out
}
